import type {
  AcquireMultipleBookHandoffData,
  BookTypeEng,
} from "@/types/acquirebook";
import { getPrefectureIdByValue } from "@/utils/utilsAcquireBook";
import type { InheritanceReuseCandidateEntity } from "@/features/inheritanceReuse/types";
import {
  normalizeRealEstateType,
  stripPrefecturePrefix,
} from "@/features/inheritanceReuse/utils/buildAcquireUrl";

/**
 * /acquiremultiplebooks へ navigate(state) で渡す形。
 * 受け手の型ガードは data/identifyName/pastDays の「キー存在」を見るため pastDays は undefined 可。
 * pastDays を undefined にすると、受け手がユーザー設定の取得経過日数で補完する。
 */
export interface MultipleAcquireNavState {
  data: AcquireMultipleBookHandoffData[];
  identifyName: string;
  pastDays: number | undefined;
}

// /acquiremultiplebooks（AcquireMultipleBooks.tsx）が location.state で期待する形:
//   { data: AcquireMultipleBookHandoffData[], identifyName: string, pastDays: number }
// AcquireMultipleBookHandoffData = { id, bookId?, bookType(BookTypeEng), prefCode, prefName,
//   locationFull, chibanKaokuNumber, kyoutan, sintaku, genzai, ownerInfo,
//   electricDrawings, tisekiDrawings, tiekiDrawings, buildingDrawings }
// 図面等のチェックは既存 feed 同様すべて false 初期。locationFull は市区町村を含む所在（prefName は別）。
// 市区町村コードは候補APIに無いため付与しない（handoff に city コード欄は無く問題なし）。

/** 候補1件を一括取得の handoff データ1件へ変換する。 */
export const toHandoffData = (
  candidate: InheritanceReuseCandidateEntity,
  id: number
): AcquireMultipleBookHandoffData => {
  const bookType: BookTypeEng =
    (normalizeRealEstateType(candidate.realEstateType) as
      | BookTypeEng
      | undefined) ?? "LAND";
  const prefCode = getPrefectureIdByValue(candidate.prefectureName);

  return {
    id,
    bookType,
    // 都道府県は prefName/prefCode で持ち、所在は市区町村を含む形（都道府県のみ除去）。
    prefCode: prefCode !== undefined ? String(prefCode) : "",
    prefName: candidate.prefectureName,
    locationFull: stripPrefecturePrefix(
      candidate.location,
      candidate.prefectureName
    ),
    chibanKaokuNumber: candidate.chibanKaokuNumber,
    kyoutan: false,
    sintaku: false,
    genzai: false,
    ownerInfo: false,
    electricDrawings: false,
    tisekiDrawings: false,
    tiekiDrawings: false,
    buildingDrawings: false,
  };
};

/**
 * 選択した候補群を /acquiremultiplebooks の location.state 形へ変換する。
 * id は 1 始まりの連番（feed の流儀に合わせる）。identifyName は空、pastDays は undefined
 * （受け手がユーザー設定の取得経過日数で補完する）。
 */
export const buildMultipleAcquireState = (
  candidates: InheritanceReuseCandidateEntity[]
): MultipleAcquireNavState => ({
  data: candidates.map((candidate, index) =>
    toHandoffData(candidate, index + 1)
  ),
  identifyName: "",
  // undefined を渡し、受け手のユーザー設定（取得経過日数）補完に委ねる。
  pastDays: undefined,
});

/**
 * サーバサイドページングでの複数選択における「選択候補の実体」をページ跨ぎで維持する。
 * 現在選択中の id（model）に含まれる実体だけを残し、現在ページ（currentRows）で選択中のものを追加する。
 * これにより別ページで選んだ行の実体も保持しつつ、選択解除された行は破棄される。
 * @param prev これまで蓄積した id→実体
 * @param model 現在の選択 id 集合（GridSelectionModel 相当）
 * @param currentRows 現在ページの候補
 */
export const mergeSelectedEntities = (
  prev: Record<string, InheritanceReuseCandidateEntity>,
  model: ReadonlyArray<string | number>,
  currentRows: InheritanceReuseCandidateEntity[]
): Record<string, InheritanceReuseCandidateEntity> => {
  const selectedIds = new Set(model.map(String));
  const next: Record<string, InheritanceReuseCandidateEntity> = {};
  for (const id of Object.keys(prev)) {
    if (selectedIds.has(id)) next[id] = prev[id];
  }
  for (const row of currentRows) {
    const id = String(row.id);
    if (selectedIds.has(id)) next[id] = row;
  }
  return next;
};

/** 選択 id 集合と id→実体マップから、選択された候補の実体配列を選択順で得る。 */
export const collectSelectedEntities = (
  model: ReadonlyArray<string | number>,
  entities: Record<string, InheritanceReuseCandidateEntity>
): InheritanceReuseCandidateEntity[] =>
  model
    .map((id) => entities[String(id)])
    .filter((row): row is InheritanceReuseCandidateEntity => row !== undefined);
